import { OnboardMode } from "@constants/misc";
import { useScrollLock } from "@hooks/useScrollLock";
import { Button, cn, CodeField } from "kz-ui-sdk";
import { PropsWithChildren, ReactNode, useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import FormTitle from "../FormTitle";

interface FormPINProps extends PropsWithChildren {
  phone?: string;
  loading?: boolean;
  mode?: OnboardMode;
  onSubmit: (values: { pin: string }) => void;
  submitLabel?: string | ReactNode;
  className?: string;
  classes?: {
    subTitle?: string;
  };
  messages?: {
    enterPIN?: string;
    reEnterPIN?: string;
  };
}

const PIN_LENGTH = 4;

const FormVerifyPIN = ({
  submitLabel,
  classes,
  phone,
  loading,
  mode,
  className,
  onSubmit,
  messages = {
    enterPIN: "Create new PIN",
    reEnterPIN: "Re-enter new PIN",
  },
}: FormPINProps) => {
  const [PINValue, setPINValue] = useState("");
  const [rePINValue, setRePINValue] = useState("");
  const { t } = useTranslation();

  const [isReenterPIN, setIsReenterPIN] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const refRetryTimes = useRef(0);

  const handleOnPINComplete = useCallback((code: string) => {
    setPINValue(code);
    setIsReenterPIN(true);
    setErrorMessage("");
  }, []);

  const handleOnRePINComplete = useCallback(
    async (code: string) => {
      if (PINValue !== code) {
        // reset re PIN
        setRePINValue("");
        setErrorMessage(t("PIN does not match. Please re-enter"));
        refRetryTimes.current += 1;
      } else {
        onSubmit({ pin: code });
        setErrorMessage("");
      }
    },
    [PINValue, onSubmit, t],
  );

  useScrollLock();

  return (
    <div className={cn("flex flex-col items-center justify-between", className)}>
      <FormTitle
        title={phone ?? ""}
        subTitle={
          <div className={cn(classes?.subTitle)}>
            {isReenterPIN ? t(messages.reEnterPIN ?? "Re-enter new PIN") : t(messages.enterPIN ?? "Create new PIN")}
          </div>
        }
      />
      {isReenterPIN ? (
        <>
          <div className="mt-6 flex flex-col items-center gap-2">
            <CodeField
              length={PIN_LENGTH}
              type={"password"}
              shouldAutoFocus
              value={rePINValue}
              onChange={setRePINValue}
              onComplete={handleOnRePINComplete}
              error={!!errorMessage}
              helperText={errorMessage}
              key={"rePIN" + refRetryTimes.current}
              helperTextClasses={{
                className: "!text-center",
              }}
              disabled={loading}
            />
          </div>
          <div className="mt-6 flex w-full flex-col items-center">
            <Button
              id="btn-next"
              variant="primary"
              size="lg"
              onClick={() => handleOnRePINComplete(rePINValue)}
              loading={loading}
              data-element-id="button-verify-pin-submit"
            >
              {submitLabel ? submitLabel : t(mode === OnboardMode.REGISTER ? "Next" : "finish & login")}
            </Button>
          </div>
        </>
      ) : (
        <>
          <div className="mt-6 flex w-full flex-col items-center">
            <CodeField
              length={PIN_LENGTH}
              type={"password"}
              shouldAutoFocus
              loading={loading}
              value={PINValue}
              onChange={setPINValue}
              onComplete={handleOnPINComplete}
              key="PIN"
            />
          </div>

          <div className="mt-6 flex w-full flex-col items-center">
            <Button
              id="btn-next"
              variant="primary"
              size="lg"
              onClick={() => handleOnPINComplete(PINValue)}
              disabled={PINValue.length < PIN_LENGTH}
              data-element-id="button-verify-pin-submit"
            >
              {t("Next")}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default FormVerifyPIN;
